/** React imports */
import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

/** Images */

/** MUI icons */

/** MUI imports */
import { Box, Typography, Grid, Divider } from "@mui/material";
import { styled } from "@mui/material/styles";

/** Global variables */
const PREFIX = "PricingCFO";

const classes = {
    body2: `${PREFIX}-body2`,
    serviceName: `${PREFIX}-serviceName`,
    subName: `${PREFIX}-subName`,
    serviceBody: `${PREFIX}-serviceBody`,
    subInfo: `${PREFIX}-subInfo`,
    roundBack: `${PREFIX}-roundBack`,
    roundBack2: `${PREFIX}-roundBack2`,
    roundBack3: `${PREFIX}-roundBack3`,
    months: `${PREFIX}-months`,
    monthsText1: `${PREFIX}-monthsText1`,
    monthsText2: `${PREFIX}-monthsText2`,
    body3: `${PREFIX}-body3`,
    textFoot: `${PREFIX}-textFoot`,
};

const StyledContainer = styled(Box)(({ theme }) => ({
    [`& .${classes.serviceName}`]: {
        fontSize: 22,
        marginBottom: 10,
    },

    [`& .${classes.subName}`]: {
        fontSize: 12,
        marginBottom: 10,
    },

    [`& .${classes.body2}`]: {
        backgroundColor: "white",
        marginTop: 15,
        padding: 30,
        minHeight: 850,
    },

    [`& .${classes.body3}`]: {
        padding: 30,
    },

    [`& .${classes.textFoot}`]: {
        padding: "15px 30px",
    },

    [`& .${classes.serviceBody}`]: {
        backgroundColor: "#F8F9F8",
        marginTop: 30,
        padding: 30,
        borderRadius: 30,
        overflow: "auto",
    },

    [`& .${classes.subInfo}`]: {
        marginTop: 30,
        fontSize: 12,
    },

    [`& .${classes.roundBack}`]: {
        backgroundColor: "#6D70AD",
        minWidth: "125px",
        marginRight: "-27px",
        justifyContent: "center",
        display: "flex",
    },

    [`& .${classes.roundBack2}`]: {
        backgroundColor: "#2D348B",
        minWidth: "130px",
        justifyContent: "center",
        display: "flex",
    },

    [`& .${classes.roundBack3}`]: {
        color: "#031851",
        minWidth: "115px",
        marginRight: "-25px",
        justifyContent: "center",
        display: "flex",
    },

    [`& .${classes.months}`]: {
        fontSize: 16,
    },
    [`& .${classes.monthsText1}`]: {
        fontSize: 16,
        justifyContent: "center",
        display: "flex",
    },
    [`& .${classes.monthsText2}`]: {
        fontSize: 16,
        justifyContent: "center",
        display: "flex",
        marginRight: "-25px",
    },
}));

// Función para convertir primera letra en mayúscula
function capitalize(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
}

export const PricingCFO = (props) => {
    /** Internationalization i18n */
    const [t] = useTranslation("global");

    /** React redux */
    const todos = useSelector((state) => state.todos);

    const { pricingCFO } = props;

    const getNamesAdd = (values) => {
        let name = "";
        if (values.additional_name === "Gobierno corporativo") {
            name = t("services.corporateGovernance");
        } else if (values.additional_name === "Presentaciones financieras") {
            name = t("services.investorPresentations");
        } else if (values.additional_name === "Reportes para inversionistas") {
            name = t("services.investorReports");
        } else if (values.additional_name === "Others") {
            name = values.other_name;
        } else {
            name = values.additional_name;
        }

        return name;
    };

    return (
        <StyledContainer>
            {pricingCFO.onboarding.isActive && (
                <Grid
                    container
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={2}
                    sx={{ paddingBottom: 1.5, paddingTop: 1.5 }}
                >
                    <Grid item xs={3}>
                        <Typography variant="h4">{t("proposals.onboard")}</Typography>
                    </Grid>
                    <Grid item xs={2}>
                        <Typography variant="body1" sx={{ textAlign: "center" }}>
                            {t(`proposals.oneTime2`)}
                        </Typography>
                    </Grid>
                    <Grid item xs={1} sx={{ textAlign: "center" }}>
                        <Typography variant="body1">1</Typography>
                    </Grid>
                    <Grid item xs={1} sx={{ textAlign: "right" }}>
                        <Typography variant="body1">
                            $
                            {parseFloat(pricingCFO.onboarding.fee).toLocaleString(todos.amountFormat, {
                                maximumFractionDigits: 2,
                            })}
                        </Typography>
                    </Grid>
                    <Grid item xs={2} sx={{ textAlign: "right" }}>
                        <Typography variant="body1">
                            $
                            {parseFloat(pricingCFO.onboarding.fee).toLocaleString(todos.amountFormat, {
                                maximumFractionDigits: 2,
                            })}
                        </Typography>
                    </Grid>
                    <Grid item xs={1} sx={{ textAlign: "center" }}>
                        <Typography variant="body1">
                            {parseFloat(pricingCFO.onboarding.discount).toLocaleString(todos.amountFormat, {
                                maximumFractionDigits: 2,
                            })}
                            %
                        </Typography>
                    </Grid>
                    <Grid item xs={2} sx={{ textAlign: "right" }}>
                        <Typography variant="body1">
                            $
                            {parseFloat(pricingCFO.onboarding.fee * (1 - pricingCFO.onboarding.discount / 100)).toLocaleString(
                                todos.amountFormat,
                                {
                                    maximumFractionDigits: 2,
                                }
                            )}
                        </Typography>
                    </Grid>
                </Grid>
            )}
            {pricingCFO.recurring.isActive && (
                <>
                    <Divider
                        sx={{
                            backgroundColor: "#031851",
                        }}
                    />
                    <Grid
                        container
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={2}
                        sx={{ paddingBottom: 1.5, paddingTop: 1.5 }}
                    >
                        <Grid item xs={3}>
                            <Typography variant="h4">
                                {t("proposals.recurring") +
                                    ` (${capitalize(pricingCFO.recurring.initDate.format("MMM YYYY")).replace(/\./g, "")})`}
                            </Typography>
                        </Grid>
                        <Grid item xs={2}>
                            <Typography variant="body1" sx={{ textAlign: "center" }}>
                                {pricingCFO.recurring.payAnnually ? t(`proposals.oneTime2`) : t("miscellaneous.monthly")}
                            </Typography>
                        </Grid>
                        <Grid item xs={1} sx={{ textAlign: "center" }}>
                            <Typography variant="body1">
                                {pricingCFO.recurring.additionals.length > 0 && !pricingCFO.recurring.onlyAdd
                                    ? "-"
                                    : pricingCFO.recurring.payAnnually
                                    ? 12
                                    : pricingCFO.recurring.qty}
                            </Typography>
                        </Grid>
                        <Grid item xs={1} sx={{ textAlign: "right" }}>
                            <Typography variant="body1">
                                {pricingCFO.recurring.additionals.length > 0 && !pricingCFO.recurring.onlyAdd
                                    ? "-"
                                    : "$" +
                                      parseFloat(pricingCFO.recurring.fee).toLocaleString(todos.amountFormat, {
                                          maximumFractionDigits: 2,
                                      })}
                            </Typography>
                        </Grid>
                        <Grid item xs={2} sx={{ textAlign: "right" }}>
                            <Typography variant="body1">
                                {pricingCFO.recurring.additionals.length > 0 && !pricingCFO.recurring.onlyAdd
                                    ? "-"
                                    : "$" +
                                      parseFloat(
                                          pricingCFO.recurring.payAnnually
                                              ? pricingCFO.recurring.fee * 12
                                              : pricingCFO.recurring.fee * pricingCFO.recurring.qty
                                      ).toLocaleString(todos.amountFormat, {
                                          maximumFractionDigits: 2,
                                      })}
                            </Typography>
                        </Grid>
                        <Grid item xs={1} sx={{ textAlign: "center" }}>
                            <Typography variant="body1">
                                {pricingCFO.recurring.additionals.length > 0 && !pricingCFO.recurring.onlyAdd
                                    ? "-"
                                    : parseFloat(pricingCFO.recurring.discount).toLocaleString(todos.amountFormat, {
                                          maximumFractionDigits: 2,
                                      }) + "%"}
                            </Typography>
                        </Grid>
                        <Grid item xs={2} sx={{ textAlign: "right" }}>
                            <Typography variant="body1">
                                $
                                {parseFloat(
                                    (pricingCFO.recurring.payAnnually
                                        ? pricingCFO.recurring.fee * 12 + pricingCFO.recurring.recuHours * pricingCFO.recurring.addFee
                                        : pricingCFO.recurring.fee * pricingCFO.recurring.qty +
                                          pricingCFO.recurring.recuHours * pricingCFO.recurring.addFee) *
                                        (1 - pricingCFO.recurring.discount / 100)
                                ).toLocaleString(todos.amountFormat, {
                                    maximumFractionDigits: 2,
                                })}
                            </Typography>
                        </Grid>
                    </Grid>
                    {pricingCFO.recurring.additionals.length > 0 && !pricingCFO.recurring.onlyAdd && (
                        <>
                            <Divider
                                sx={{
                                    backgroundColor: "#031851",
                                }}
                            />
                            <Grid
                                container
                                alignItems="center"
                                justifyContent="space-between"
                                spacing={2}
                                sx={{ paddingBottom: 1.5, paddingTop: 1.5 }}
                            >
                                <Grid item xs={3}>
                                    <Typography variant="body1" sx={{ paddingLeft: 6, wordBreak: "break-word" }}>
                                        {t("proposals.coreServ")}
                                    </Typography>
                                </Grid>
                                <Grid item xs={2}></Grid>
                                <Grid item xs={1} sx={{ textAlign: "center" }}>
                                    <Typography variant="body1">
                                        {pricingCFO.recurring.payAnnually ? 12 : pricingCFO.recurring.qty}
                                    </Typography>
                                </Grid>
                                <Grid item xs={1} sx={{ textAlign: "right" }}>
                                    <Typography variant="body1">
                                        $
                                        {parseFloat(pricingCFO.recurring.fee).toLocaleString(todos.amountFormat, {
                                            maximumFractionDigits: 2,
                                        })}
                                    </Typography>
                                </Grid>
                                <Grid item xs={2} sx={{ textAlign: "right" }}>
                                    <Typography variant="body1">
                                        $
                                        {parseFloat(
                                            pricingCFO.recurring.payAnnually
                                                ? pricingCFO.recurring.fee * 12
                                                : pricingCFO.recurring.fee * pricingCFO.recurring.qty
                                        ).toLocaleString(todos.amountFormat, {
                                            maximumFractionDigits: 2,
                                        })}
                                    </Typography>
                                </Grid>
                                <Grid item xs={1} sx={{ textAlign: "center" }}>
                                    <Typography variant="body1">
                                        {parseFloat(pricingCFO.recurring.discount).toLocaleString(todos.amountFormat, {
                                            maximumFractionDigits: 2,
                                        })}
                                        %
                                    </Typography>
                                </Grid>
                                <Grid item xs={2} sx={{ textAlign: "right" }}>
                                    <Typography variant="body1">
                                        $
                                        {parseFloat(
                                            (pricingCFO.recurring.payAnnually
                                                ? pricingCFO.recurring.fee * 12
                                                : pricingCFO.recurring.fee * pricingCFO.recurring.qty) *
                                                (1 - pricingCFO.recurring.discount / 100)
                                        ).toLocaleString(todos.amountFormat, {
                                            maximumFractionDigits: 2,
                                        })}
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Divider
                                sx={{
                                    backgroundColor: "#031851",
                                }}
                            />
                            <Grid
                                container
                                alignItems="center"
                                justifyContent="space-between"
                                spacing={2}
                                sx={{ paddingBottom: 1.5, paddingTop: 1.5 }}
                            >
                                <Grid item xs={3}>
                                    <Typography variant="body1" sx={{ paddingLeft: 6, wordBreak: "break-word" }}>
                                        {t("proposals.addons")}
                                    </Typography>
                                </Grid>
                                <Grid item xs={2}></Grid>
                                <Grid item xs={1} sx={{ textAlign: "center" }}>
                                    <Typography variant="body1">{pricingCFO.recurring.recuHours}</Typography>
                                </Grid>
                                <Grid item xs={1} sx={{ textAlign: "right" }}>
                                    <Typography variant="body1">
                                        $
                                        {parseFloat(pricingCFO.recurring.addFee).toLocaleString(todos.amountFormat, {
                                            maximumFractionDigits: 2,
                                        })}
                                    </Typography>
                                </Grid>
                                <Grid item xs={2} sx={{ textAlign: "right" }}>
                                    <Typography variant="body1">
                                        $
                                        {parseFloat(pricingCFO.recurring.recuHours * pricingCFO.recurring.addFee).toLocaleString(
                                            todos.amountFormat,
                                            {
                                                maximumFractionDigits: 2,
                                            }
                                        )}
                                    </Typography>
                                </Grid>
                                <Grid item xs={1} sx={{ textAlign: "center" }}>
                                    <Typography variant="body1">
                                        {parseFloat(pricingCFO.recurring.discount).toLocaleString(todos.amountFormat, {
                                            maximumFractionDigits: 2,
                                        })}
                                        %
                                    </Typography>
                                </Grid>
                                <Grid item xs={2} sx={{ textAlign: "right" }}>
                                    <Typography variant="body1">
                                        $
                                        {parseFloat(
                                            pricingCFO.recurring.recuHours *
                                                pricingCFO.recurring.addFee *
                                                (1 - pricingCFO.recurring.discount / 100)
                                        ).toLocaleString(todos.amountFormat, {
                                            maximumFractionDigits: 2,
                                        })}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </>
                    )}
                    {pricingCFO.recurring.additionals.map(
                        (value) =>
                            value.additional_name !== "CFO" && (
                                <>
                                    <Divider
                                        sx={{
                                            backgroundColor: "#031851",
                                        }}
                                    />
                                    <Grid
                                        container
                                        alignItems="center"
                                        justifyContent="space-between"
                                        spacing={2}
                                        sx={{ paddingBottom: 1, paddingTop: 1 }}
                                    >
                                        <Grid item xs={3}>
                                            <Typography
                                                variant="body1"
                                                sx={{ paddingLeft: 8, wordBreak: "break-word" }}
                                            >
                                                {value.other_name ?? getNamesAdd(value)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={2}></Grid>
                                        <Grid item xs={1} sx={{ textAlign: "center" }}>
                                            <Typography variant="body1">{value.hours}</Typography>
                                        </Grid>
                                        <Grid item xs={1} sx={{ textAlign: "right" }}>
                                            <Typography variant="body1">
                                                $
                                                {parseFloat(pricingCFO.recurring.addFee).toLocaleString(todos.amountFormat, {
                                                    maximumFractionDigits: 2,
                                                })}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={2} sx={{ textAlign: "right" }}>
                                            <Typography variant="body1">
                                                $
                                                {parseFloat(pricingCFO.recurring.addFee * value.hours).toLocaleString(
                                                    todos.amountFormat,
                                                    {
                                                        maximumFractionDigits: 2,
                                                    }
                                                )}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={1} sx={{ textAlign: "center" }}>
                                            <Typography variant="body1">
                                                {parseFloat(pricingCFO.recurring.discount).toLocaleString(
                                                    todos.amountFormat,
                                                    {
                                                        maximumFractionDigits: 2,
                                                    }
                                                )}
                                                %
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={2} sx={{ textAlign: "right" }}>
                                            <Typography variant="body1">
                                                $
                                                {parseFloat(
                                                    pricingCFO.recurring.addFee *
                                                        value.hours *
                                                        (1 - pricingCFO.recurring.discount / 100)
                                                ).toLocaleString(todos.amountFormat, {
                                                    maximumFractionDigits: 2,
                                                })}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </>
                            )
                    )}
                </>
            )}

            {pricingCFO.catchUp.isActive && (
                <>
                    <Divider
                        sx={{
                            backgroundColor: "#031851",
                        }}
                    />
                    <Grid
                        container
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={2}
                        sx={{ paddingBottom: 1.5, paddingTop: 1.5 }}
                    >
                        <Grid item xs={3}>
                            <Typography variant="h4">
                                {t("invoicing.catchUp") +
                                    ` (${
                                        capitalize(pricingCFO.catchUp.initDate.format("MMM")).replace(/\./g, "") +
                                        "-" +
                                        capitalize(pricingCFO.catchUp.untilDate.format("MMM YYYY")).replace(/\./g, "")
                                    })`}
                            </Typography>
                        </Grid>
                        <Grid item xs={2}>
                            <Typography variant="body1" sx={{ textAlign: "center" }}>
                                {t("invoicing.project")}
                            </Typography>
                        </Grid>
                        <Grid item xs={1} sx={{ textAlign: "center" }}>
                            <Typography variant="body1">
                                {pricingCFO.catchUp.additionals.length > 0 && !pricingCFO.catchUp.onlyAdd
                                    ? "-"
                                    : pricingCFO.catchUp.monthQty}
                            </Typography>
                        </Grid>
                        <Grid item xs={1} sx={{ textAlign: "right" }}>
                            <Typography variant="body1">
                                {pricingCFO.catchUp.additionals.length > 0 && !pricingCFO.catchUp.onlyAdd
                                    ? "-"
                                    : "$" +
                                      parseFloat(pricingCFO.catchUp.fee).toLocaleString(todos.amountFormat, {
                                          maximumFractionDigits: 2,
                                      })}
                            </Typography>
                        </Grid>
                        <Grid item xs={2} sx={{ textAlign: "right" }}>
                            <Typography variant="body1">
                                {pricingCFO.catchUp.additionals.length > 0 && !pricingCFO.catchUp.onlyAdd
                                    ? "-"
                                    : "$" +
                                      parseFloat(pricingCFO.catchUp.fee * pricingCFO.catchUp.monthQty).toLocaleString(
                                          todos.amountFormat,
                                          {
                                              maximumFractionDigits: 2,
                                          }
                                      )}
                            </Typography>
                        </Grid>
                        <Grid item xs={1} sx={{ textAlign: "center" }}>
                            <Typography variant="body1">
                                {pricingCFO.catchUp.additionals.length > 0 && !pricingCFO.catchUp.onlyAdd
                                    ? "-"
                                    : parseFloat(pricingCFO.catchUp.discount).toLocaleString(todos.amountFormat, {
                                          maximumFractionDigits: 2,
                                      }) + "%"}
                            </Typography>
                        </Grid>
                        <Grid item xs={2} sx={{ textAlign: "right" }}>
                            <Typography variant="body1">
                                $
                                {parseFloat(
                                    (pricingCFO.catchUp.fee * pricingCFO.catchUp.monthQty +
                                        pricingCFO.catchUp.totalHours * pricingCFO.catchUp.addFee) *
                                        (1 - pricingCFO.catchUp.discount / 100)
                                ).toLocaleString(todos.amountFormat, {
                                    maximumFractionDigits: 2,
                                })}
                            </Typography>
                        </Grid>
                    </Grid>
                    {pricingCFO.catchUp.additionals.length > 0 && !pricingCFO.catchUp.onlyAdd && (
                        <>
                            <Divider
                                sx={{
                                    backgroundColor: "#031851",
                                }}
                            />
                            <Grid
                                container
                                alignItems="center"
                                justifyContent="space-between"
                                spacing={2}
                                sx={{ paddingBottom: 1.5, paddingTop: 1.5 }}
                            >
                                <Grid item xs={3}>
                                    <Typography variant="body1" sx={{ paddingLeft: 6, wordBreak: "break-word" }}>
                                        {t("proposals.coreServ")}
                                    </Typography>
                                </Grid>
                                <Grid item xs={2}></Grid>
                                <Grid item xs={1} sx={{ textAlign: "center" }}>
                                    <Typography variant="body1">{pricingCFO.catchUp.monthQty}</Typography>
                                </Grid>
                                <Grid item xs={1} sx={{ textAlign: "right" }}>
                                    <Typography variant="body1">
                                        $
                                        {parseFloat(pricingCFO.catchUp.fee).toLocaleString(todos.amountFormat, {
                                            maximumFractionDigits: 2,
                                        })}
                                    </Typography>
                                </Grid>
                                <Grid item xs={2} sx={{ textAlign: "right" }}>
                                    <Typography variant="body1">
                                        $
                                        {parseFloat(pricingCFO.catchUp.fee * pricingCFO.catchUp.monthQty).toLocaleString(
                                            todos.amountFormat,
                                            {
                                                maximumFractionDigits: 2,
                                            }
                                        )}
                                    </Typography>
                                </Grid>
                                <Grid item xs={1} sx={{ textAlign: "center" }}>
                                    <Typography variant="body1">
                                        {parseFloat(pricingCFO.catchUp.discount).toLocaleString(todos.amountFormat, {
                                            maximumFractionDigits: 2,
                                        })}
                                        %
                                    </Typography>
                                </Grid>
                                <Grid item xs={2} sx={{ textAlign: "right" }}>
                                    <Typography variant="body1">
                                        $
                                        {parseFloat(
                                            pricingCFO.catchUp.fee * pricingCFO.catchUp.monthQty * (1 - pricingCFO.catchUp.discount / 100)
                                        ).toLocaleString(todos.amountFormat, {
                                            maximumFractionDigits: 2,
                                        })}
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Divider
                                sx={{
                                    backgroundColor: "#031851",
                                }}
                            />
                            <Grid
                                container
                                alignItems="center"
                                justifyContent="space-between"
                                spacing={2}
                                sx={{ paddingBottom: 1.5, paddingTop: 1.5 }}
                            >
                                <Grid item xs={3}>
                                    <Typography variant="body1" sx={{ paddingLeft: 6, wordBreak: "break-word" }}>
                                        {t("proposals.addons")}
                                    </Typography>
                                </Grid>
                                <Grid item xs={2}></Grid>
                                <Grid item xs={1} sx={{ textAlign: "center" }}>
                                    <Typography variant="body1">{pricingCFO.catchUp.totalHours}</Typography>
                                </Grid>
                                <Grid item xs={1} sx={{ textAlign: "right" }}>
                                    <Typography variant="body1">
                                        $
                                        {parseFloat(pricingCFO.catchUp.addFee).toLocaleString(todos.amountFormat, {
                                            maximumFractionDigits: 2,
                                        })}
                                    </Typography>
                                </Grid>
                                <Grid item xs={2} sx={{ textAlign: "right" }}>
                                    <Typography variant="body1">
                                        $
                                        {parseFloat(pricingCFO.catchUp.totalHours * pricingCFO.catchUp.addFee).toLocaleString(
                                            todos.amountFormat,
                                            {
                                                maximumFractionDigits: 2,
                                            }
                                        )}
                                    </Typography>
                                </Grid>
                                <Grid item xs={1} sx={{ textAlign: "center" }}>
                                    <Typography variant="body1">
                                        {parseFloat(pricingCFO.catchUp.discount).toLocaleString(todos.amountFormat, {
                                            maximumFractionDigits: 2,
                                        })}
                                        %
                                    </Typography>
                                </Grid>
                                <Grid item xs={2} sx={{ textAlign: "right" }}>
                                    <Typography variant="body1">
                                        $
                                        {parseFloat(
                                            pricingCFO.catchUp.totalHours *
                                                pricingCFO.catchUp.addFee *
                                                (1 - pricingCFO.catchUp.discount / 100)
                                        ).toLocaleString(todos.amountFormat, {
                                            maximumFractionDigits: 2,
                                        })}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </>
                    )}
                    {pricingCFO.catchUp.additionals.map(
                        (value) =>
                            value.additional_name !== "CFO" && (
                                <>
                                    <Divider
                                        sx={{
                                            backgroundColor: "#031851",
                                        }}
                                    />
                                    <Grid
                                        container
                                        alignItems="center"
                                        justifyContent="space-between"
                                        spacing={2}
                                        sx={{ paddingBottom: 1, paddingTop: 1 }}
                                    >
                                        <Grid item xs={3}>
                                            <Typography
                                                variant="body1"
                                                sx={{ paddingLeft: 8, wordBreak: "break-word" }}
                                            >
                                                {value.other_name ?? getNamesAdd(value)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={2}></Grid>
                                        <Grid item xs={1} sx={{ textAlign: "center" }}>
                                            <Typography variant="body1">{value.hours}</Typography>
                                        </Grid>
                                        <Grid item xs={1} sx={{ textAlign: "right" }}>
                                            <Typography variant="body1">
                                                $
                                                {parseFloat(pricingCFO.catchUp.addFee).toLocaleString(todos.amountFormat, {
                                                    maximumFractionDigits: 2,
                                                })}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={2} sx={{ textAlign: "right" }}>
                                            <Typography variant="body1">
                                                $
                                                {parseFloat(pricingCFO.catchUp.addFee * value.hours).toLocaleString(
                                                    todos.amountFormat,
                                                    {
                                                        maximumFractionDigits: 2,
                                                    }
                                                )}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={1} sx={{ textAlign: "center" }}>
                                            <Typography variant="body1">
                                                {parseFloat(pricingCFO.catchUp.discount).toLocaleString(todos.amountFormat, {
                                                    maximumFractionDigits: 2,
                                                })}
                                                %
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={2} sx={{ textAlign: "right" }}>
                                            <Typography variant="body1">
                                                $
                                                {parseFloat(
                                                    pricingCFO.catchUp.addFee *
                                                        value.hours *
                                                        (1 - pricingCFO.catchUp.discount / 100)
                                                ).toLocaleString(todos.amountFormat, {
                                                    maximumFractionDigits: 2,
                                                })}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </>
                            )
                    )}
                </>
            )}

            {pricingCFO.consulting.isActive &&
                pricingCFO.consulting.array.map((consult) => (
                    <>
                        <Divider
                            sx={{
                                backgroundColor: "#031851",
                            }}
                        />
                        <Grid
                            container
                            alignItems="center"
                            justifyContent="space-between"
                            spacing={2}
                            sx={{ paddingBottom: 1.5, paddingTop: 1.5 }}
                        >
                            <Grid item xs={3}>
                                <Typography variant="h4">{consult.description}</Typography>
                            </Grid>
                            <Grid item xs={2}>
                                <Typography variant="body1" sx={{ textAlign: "center" }}>
                                    {t(`proposals.oneTime2`)}
                                </Typography>
                            </Grid>
                            <Grid item xs={1} sx={{ textAlign: "center" }}>
                                <Typography variant="body1">{consult.hours}</Typography>
                            </Grid>
                            <Grid item xs={1} sx={{ textAlign: "right" }}>
                                <Typography variant="body1">
                                    $
                                    {parseFloat(pricingCFO.consulting.fee).toLocaleString(todos.amountFormat, {
                                        maximumFractionDigits: 2,
                                    })}
                                </Typography>
                            </Grid>
                            <Grid item xs={2} sx={{ textAlign: "right" }}>
                                <Typography variant="body1">
                                    $
                                    {parseFloat(pricingCFO.consulting.fee * consult.hours).toLocaleString(
                                        todos.amountFormat,
                                        {
                                            maximumFractionDigits: 2,
                                        }
                                    )}
                                </Typography>
                            </Grid>
                            <Grid item xs={1} sx={{ textAlign: "center" }}>
                                <Typography variant="body1">
                                    {parseFloat(consult.discount).toLocaleString(todos.amountFormat, {
                                        maximumFractionDigits: 2,
                                    })}
                                    %
                                </Typography>
                            </Grid>
                            <Grid item xs={2} sx={{ textAlign: "right" }}>
                                <Typography variant="body1">
                                    $
                                    {parseFloat(
                                        pricingCFO.consulting.fee * consult.hours * (1 - consult.discount / 100)
                                    ).toLocaleString(todos.amountFormat, {
                                        maximumFractionDigits: 2,
                                    })}
                                </Typography>
                            </Grid>
                        </Grid>
                    </>
                ))}
        </StyledContainer>
    );
};
